<template>
  <div
    :class="{'search-input-multiple--active': isDropdownOpen, 'search-input-multiple--with-image': image}"
    class="search-input-multiple">
    <base-select
      :items="visibleSearchResults"
      :should-hide-selected-value="false"
      :disabled="disabled"
      @open="openItems"
      @close="closeItems">
      <template #value="slotProps">
        <div class="search-input-multiple__selector">
          <img
            v-if="image"
            width="24"
            height="24"
            :src="image"
            alt=""
            class="search-input-multiple__image">

          <ui-input
            ref="uiInput"
            v-model="searchValue"
            :title="title"
            :error="error"
            :placeholder="placeholder"
            :disabled="disabled"
            class="search-input-multiple__input"
            @focus="$emit('focus')"
            @blur="handleBlur"
            @click.native.stop="slotProps.openItems" />

          <div class="search-input-multiple__icons">
            <div
              v-if="currentValues.length > 0"
              class="search-input-multiple__cross shape-cross"
              @click.stop="clearValues" />
            <transition
              name="transition--opacity"
              mode="out-in">
              <img
                v-if="isDropdownOpen"
                width="16"
                height="16"
                alt="search icon"
                src="/images/icons/search.svg"
                class="search-input-multiple__icon">

              <arrowhead-icon
                v-if="!isDropdownOpen"
                class="search-input-multiple__icon"
                :is-reverse="isDropdownOpen" />
            </transition>
          </div>
        </div>
      </template>

      <template #options="slotProps">
        <div
          ref="resultsContainer"
          class="search-input-multiple__results">
          <div
            v-for="(item, index) in slotProps.availableItems"
            :key="`${titleField ? item[titleField] : item}-${index}`"
            class="search-input-multiple__result"
            @click="selectItem(item)">
            <img
              v-show="item.image"
              width="24"
              height="24"
              :src="item.image"
              alt=""
              class="search-input-multiple__result-image">

            <span
              class="search-input-multiple__result-title"
              :title="titleField ? item[titleField] : item">
              {{ titleField ? item[titleField] : item }}
            </span>

            <ui-checkbox
              class="search-input-multiple__result-checkbox"
              :value="isItemChecked(item)"
              @click.native.prevent />
          </div>
          <div
            v-if="hasMoreResults"
            v-intersected="{
              handler: loadMoreResults
            }"
            class="search-input-multiple__load-more">
            Loading more...
          </div>
        </div>
      </template>
    </base-select>
  </div>
</template>

<script>
import UiInput from 'Components/ui/Input/UiInput.vue';
import BaseSelect from 'Components/ui/Select/BaseSelect.vue';
import ArrowheadIcon from 'Components/ui/Icons/ArrowheadIcon.vue';
import UiCheckbox from 'Components/ui/Checkbox/UiCheckbox.vue';
import intersectedDirective from 'Root/directives/intersectedDirective';

export default {
  name: 'MultipleSearchInput',

  components: {
    UiCheckbox,
    ArrowheadIcon,
    BaseSelect,
    UiInput,
  },

  directives: {
    ...intersectedDirective,
  },

  props: {
    searchResults: {
      type: Array,
      default: () => [],
    },

    values: {
      type: Array,
      default: () => [],
    },

    title: {
      type: String,
      default: '',
    },

    titleField: {
      type: String,
      default: null,
    },

    error: {
      type: [String, Boolean],
      default: null,
    },

    placeholder: {
      type: String,
      default: '',
    },

    shouldClearEmptyValue: {
      type: Boolean,
      default: true,
    },

    shouldEmitEmptySearch: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    alwaysShowResults: {
      type: Boolean,
      default: false,
    },

    // Emit only one value
    isRadio: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isDropdownOpen: false,
      debounceTimer: null,
      searchValue: '',

      currentValues: [],

      // Иконка слева.
      // Показываем картинку первого выбранного элемента
      image: null,

      visibleResults: 10,
      isLoading: false,

      isDisplayingSelectedTitle: false,
      isDisplayingCounter: false,
    };
  },

  computed: {
    visibleSearchResults() {
      return this.searchResults.slice(0, this.visibleResults);
    },

    hasMoreResults() {
      return this.visibleResults < this.searchResults.length;
    },
  },

  watch: {
    searchValue: {
      handler(newValue) {
        if (this.isDisplayingSelectedTitle || this.isDisplayingCounter) {
          if (newValue !== this.getItemTitle(this.currentValues[0]) && !newValue.startsWith('+')) {
            this.isDisplayingSelectedTitle = false;
            this.isDisplayingCounter = false;
          } else {
            this.$emit('input', this.currentValues);
            return;
          }
        }

        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          if (this.shouldEmitEmptySearch && !newValue) {
            this.emitSearch();
          }

          if (newValue && !this.isDisplayingSelectedTitle && !this.isDisplayingCounter) {
            this.emitSearch();
          } else if (this.shouldClearEmptyValue && !newValue) {
            this.emitValues();
          }
        }, 300);
      },
    },
  },

  created() {
    this.currentValues = [...this.values];
    this.updateImage();
    this.restoreDisplayValue();
  },

  methods: {
    setSearchValue() {
      if (this.currentValues.length === 1) {
        this.searchValue = this.getItemTitle(this.currentValues[0]);
        this.isDisplayingSelectedTitle = true;
        this.isDisplayingCounter = false;
      } else if (this.currentValues.length > 1) {
        this.searchValue = `+${this.currentValues.length - 1}`;
        this.isDisplayingSelectedTitle = false;
        this.isDisplayingCounter = true;
      } else {
        this.searchValue = '';
        this.isDisplayingSelectedTitle = false;
        this.isDisplayingCounter = false;
      }
      this.$emit('input', this.currentValues);
    },

    getItemTitle(item) {
      return this.titleField ? item[this.titleField] : item.toString();
    },

    loadMoreResults(entries) {
      if (entries[0].isIntersecting && !this.isLoading && this.hasMoreResults) {
        this.isLoading = true;
        setTimeout(() => {
          this.visibleResults += 10;
          this.isLoading = false;
        }, 500);
      }
    },

    emitSearch() {
      if (this.isDisplayingSelectedTitle || this.isDisplayingCounter) {
        this.$emit('input', this.currentValues);
        return;
      }

      if (this.searchValue.startsWith('+')) {
        return;
      }

      this.resetVisibleResults();
      this.$emit('search', this.searchValue);
    },

    emitValues() {
      this.$emit('input', this.currentValues);
    },

    selectItem(item) {
      if (this.isRadio) {
        this.currentValues = [item];
      } else {
        const isItemInList = this.currentValues.some((value) => JSON.stringify(value) === JSON.stringify(item));

        if (isItemInList) {
          this.currentValues = this.currentValues.filter((value) => JSON.stringify(value) !== JSON.stringify(item));
        } else {
          this.currentValues.push(item);
        }
      }

      this.updateImage();
      this.setSearchValue();
    },

    openItems() {
      if (this.isDropdownOpen || this.disabled) {
        return;
      }
      this.isDropdownOpen = true;
      this.$refs.uiInput.$refs.input.focus();

      if (!this.isDisplayingSelectedTitle && !this.isDisplayingCounter) {
        this.emitSearch();
      }

      this.resetVisibleResults();
    },

    closeItems() {
      this.isDropdownOpen = false;
      this.setSearchValue();
      this.$refs.uiInput.$refs.input.blur();
      this.$emit('search', '');
    },

    clearValues() {
      this.currentValues = [];
      this.searchValue = '';
      this.isDisplayingSelectedTitle = false;
      this.isDisplayingCounter = false;
      this.updateImage();
      this.emitValues();
      this.emitSearch();

      this.$nextTick(() => {
        this.$refs.uiInput.$refs.input.focus();
      });
    },

    isItemChecked(item) {
      return this.currentValues.some((value) => JSON.stringify(value) === JSON.stringify(item));
    },

    updateImage() {
      if (this.currentValues.length) {
        this.image = this.currentValues[0].image;
      } else {
        this.image = null;
      }
    },

    resetVisibleResults() {
      this.visibleResults = 10;
    },

    restoreDisplayValue() {
      if (this.currentValues.length > 0 && !this.searchValue) {
        this.setSearchValue();
      }
    },

    handleBlur() {
      this.$emit('blur');
      this.restoreDisplayValue();
    },
  },
};
</script>

<style lang="scss" scoped>
.search-input-multiple {
  --border: 1px solid #{rgba($color--basic-blue, 0.25)};
  --border-radius: 4px;

  --input-bg-color: #{rgba($color--basic-blue, 0.25)};
  --input-bg-color-active: #{rgba($color--basic-blue, 0.45)};
  --input-height: 32px;
  --input-padding: 9px 12px;
  --input-placeholder-color: $color--basic-white;
  --input-color: $color--basic-white;
  --input-font-size: 13px;
  --input-font-weight: 500;

  // Не стал делать прозрачный фон, как в макете, ибо это выглядит всрато.
  --dropdown-bg-color: #303c70;
  --dropdown-padding: 4px;
  --dropdown-elements-padding: 0 8px;
  --dropdown-elements-height: 28px;
}

.search-input-multiple--with-image {
  --input-padding: 9px 12px 9px 32px;
}

.search-input-multiple__image {
  position: absolute;
  left: 4px;
  top: 50%;
  width: 24px;
  height: 24px;
  transform: translateY(-50%);
  object-fit: cover;
}

.search-input-multiple__load-more {
  margin-top: 4px;
  padding: var(--dropdown-elements-padding);
}

.search-input-multiple__input {
  --height: var(--input-height);
  --background-color: var(--input-bg-color, #{$color--additional-menu});
  --padding: var(--input-padding, 8px 20px);
  --placeholder-color: var(--input-placeholder-color, #{$color--basic-light-gray});
  --color: var(--input-color, #{$color--basic-light-gray});
  --font-size: var(--input-font-size, 15px);
  --font-weight: var(--input-font-weight, 400);
  --opacity--focused: 1;
  --background-color--focused: var(--input-bg-color, #{$color--additional-menu});

  cursor: pointer;

  &--has-value {
    --background-color: var(--input-bg-color-active, #{$color--additional-menu});
  }
}

.search-input-multiple__input /deep/ .ui-input__body {
  margin-right: 22px; // Фиксит проваливание ошибки в стрелку селектора
}

.search-input-multiple__icon {
  min-width: 16px;
  height: 16px;
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
}

.search-input-multiple__results {
  position: relative;
  padding: var(--dropdown-padding, 4px);
  border-radius: var(--border-radius);
  border: var(--border, none);
  background: var(--dropdown-bg-color, #{$color--additional-menu});
  max-height: 200px;
  overflow-y: auto;
  transform: translateY(2px);
  scrollbar-color: #{rgba($color--basic-blue, 0.1)} transparent;
  scrollbar-width: thin;
  overscroll-behavior: contain;
}

.search-input-multiple__icons {
  display: flex;
  align-items: center;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.search-input-multiple__cross {
  --shape-color: #{$color--basic-white};
  --thickness: 2px;
  --transform-before: translate(-50%, -50%) rotate(-45deg);
  --transform-after: translate(-50%, -50%) rotate(45deg);

  width: 16px;
  height: 16px;
  margin-right: 35px;
  flex-shrink: 0;
}

.search-input-multiple__icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.search-input-multiple__input /deep/ .ui-input__body {
  margin-right: 46px; // Increased to accommodate both icons
}

.search-input-multiple__result {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  border-radius: var(--border-radius);
  padding: var(--dropdown-elements-padding);
  height: var(--dropdown-elements-height);
  cursor: pointer;
  color: $color--basic-white;
  transition: background-color .25s cubic-bezier(0, 0, .45, 1);
  font-weight: 500;
  background: transparent;

  &:hover {
    background: #5F73FA40;
  }

  .search-input-multiple__result-image {
    margin-right: 8px;
    width: 24px;
    object-fit: contain;
  }

  .search-input-multiple__result-title {
    overflow: hidden;
    text-overflow: ellipsis;
    text-wrap: nowrap;
  }

  .search-input-multiple__result-checkbox {
    margin-left: auto;

    --size: 20px;
    --check-size: 10px;
    --bg-color: #5F73FA40;
    --bg-color-checked: linear-gradient(270deg, #5F73FA 0%, #7B61FF 100%);
    --check-color: #{$color--basic-white};
    --border-radius: 3px;

    /deep/ .ui-checkbox-box {
      margin-right: 0;
    }
  }
}

@media screen and (min-height: 600px) {
  .search-input-multiple__results {
    max-height: 300px;
  }
}
</style>
